<template>
  <section
    :class="[
      'rounded-md',
      borderColor,
      backgroundColor,
      { 'border-b': !isOpen },
    ]"
  >
    <div
      class="flex items-center justify-between px-2 py-2 text-xs font-semibold text-gray-700"
      @click="toggle"
      :class="[borderColor]"
    >
      <div class="flex items-center gap-2">
        <slot
          name="icon"
          v-if="$slots.icon"
        />
        <span class="py-2 text-lg font-medium text-steel-900">{{ title }}</span>
      </div>
      <svg
        class="h-5 w-5 cursor-pointer transition-transform duration-200"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path
          d="M6.343 7.757 4.929 9.172 12 16.243l7.071-7.071-1.414-1.415L12 13.414 6.343 7.757Z"
        />
      </svg>
    </div>

    <transition
      @before-enter="beforeEnter"
      @enter="enter"
      @after-enter="afterEnter"
      @before-leave="beforeLeave"
      @leave="leave"
      @after-leave="afterLeave"
    >
      <div
        v-show="isOpen"
        class="px-2 text-xs text-body-color"
      >
        <slot />
      </div>
    </transition>
  </section>
</template>

<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  title: { type: String, required: true },
  modelValue: { type: Boolean, default: true },
  borderColor: { type: String, default: 'border-light-gray' },
  backgroundColor: { type: String, default: 'bg-white' },
  duration: { type: Number, default: 300 },
});

const emit = defineEmits(['update:modelValue']);
const isOpen = ref(props.modelValue);

watch(
  () => props.modelValue,
  (v) => (isOpen.value = v)
);
watch(isOpen, (v) => emit('update:modelValue', v));
const toggle = () => (isOpen.value = !isOpen.value);

const setTransition = (el) => {
  el.style.transition = `height ${props.duration}ms ease, opacity ${props.duration}ms ease`;
};

// ----- ENTER -----
const beforeEnter = (el) => {
  el.style.height = '0';
  el.style.opacity = '0';
  el.style.overflow = 'hidden';
};

const enter = (el) => {
  // force first reflow so starting height (0) is taken
  void el.offsetHeight;
  setTransition(el);
  el.style.height = el.scrollHeight + 'px';
  el.style.opacity = '1';
};

const afterEnter = (el) => {
  el.style.height = 'auto'; // reset
  el.style.transition = '';
  el.style.overflow = 'visible';
};

// ----- LEAVE -----
const beforeLeave = (el) => {
  el.style.height = el.scrollHeight + 'px'; // fixed height to start
  el.style.opacity = '1';
  el.style.overflow = 'hidden';
};

const leave = (el) => {
  // force reflow before collapsing
  void el.offsetHeight;
  setTransition(el);
  el.style.height = '0';
  el.style.opacity = '0';
};

const afterLeave = (el) => {
  el.style.transition = '';
};
</script>

<style scoped>
.rotate-180 {
  transform: rotate(180deg);
}

.activeGray {
  border-color: rgba(31, 36, 46, 0.14);
}
</style>
